'use client';

/**
 * Expert Review Session intake (post-payment step).
 * Replaces the TidyCal embed: captures contact info, website, focus and
 * preferred time windows; the slot is confirmed by email until the in-house
 * calendar environment ships (a calendar step will replace the windows).
 */

import { useEffect, useState, Suspense } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Link } from '@/i18n/routing';
import { CheckCircle, Clock, FileText, Loader2, ArrowRight, Mail } from 'lucide-react';
import Logo from '@/components/layout/Logo';
import styles from './page.module.css';

function IntakeForm() {
  const t = useTranslations('discoveryCallIntake');
  const locale = useLocale();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('https://');
  const [focusNotes, setFocusNotes] = useState('');
  const [timezone, setTimezone] = useState('');
  const [timezones, setTimezones] = useState<string[]>([]);
  const [preferredWindows, setPreferredWindows] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const intlAny = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
      const list =
        typeof intlAny.supportedValuesOf === 'function' ? intlAny.supportedValuesOf('timeZone') : [];
      setTimezones(list);
      if (detected && (list.length === 0 || list.includes(detected))) {
        setTimezone(detected);
      } else if (list.length > 0) {
        setTimezone(list[0]);
      }
    } catch {
      // Leave the timezone empty; the field stays editable
    }
  }, []);

  // Always submit a single, well-formed https:// URL, even when the visitor
  // pastes a value that already includes a protocol.
  const ensureHttps = (raw: string) => 'https://' + raw.trim().replace(/^\s*https?:\/\//i, '');
  const hasHost = ensureHttps(websiteUrl).length > 'https://'.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/discovery-call/intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          websiteUrl: ensureHttps(websiteUrl),
          focusNotes,
          timezone,
          preferredWindows,
          locale,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || t('form.genericError'));
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('form.genericError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    const waveUrl = process.env.NEXT_PUBLIC_WAVE_EXPERT_REVIEW_URL || '';
    return (
      <div className={styles.confirmation}>
        <div className={styles.confirmIcon}>
          <CheckCircle size={48} />
        </div>
        <h1 className={styles.title}>{t('confirmation.title')}</h1>
        <p className={styles.subtitle}>{t('confirmation.subtitle')}</p>
        {waveUrl && (
          <a
            href={waveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.submitButton}
          >
            {t('confirmation.payButton')}
            <ArrowRight size={18} />
          </a>
        )}
        <ul className={styles.confirmList}>
          <li>
            <Mail size={16} />
            <span>{t('confirmation.scheduling')}</span>
          </li>
          <li>
            <FileText size={16} />
            <span>{t('confirmation.report')}</span>
          </li>
          <li>
            <Clock size={16} />
            <span>{t('confirmation.reschedule')}</span>
          </li>
        </ul>
        <Link href="/" className={styles.homeLink}>
          {t('confirmation.backToHome')}
        </Link>
      </div>
    );
  }

  return (
    <>
      <span className={styles.stepLabel}>{t('stepLabel')}</span>
      <h1 className={styles.title}>{t('title')}</h1>
      <p className={styles.subtitle}>{t('subtitle')}</p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <label htmlFor="name" className={styles.label}>{t('form.nameLabel')}</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('form.namePlaceholder')}
              className={styles.input}
              disabled={isLoading}
              maxLength={120}
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="email" className={styles.label}>{t('form.emailLabel')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('form.emailPlaceholder')}
              required
              className={styles.input}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="websiteUrl" className={styles.label}>{t('form.websiteLabel')}</label>
          <input
            type="text"
            inputMode="url"
            id="websiteUrl"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            onPaste={(e) => {
              e.preventDefault();
              setWebsiteUrl(ensureHttps(e.clipboardData.getData('text')));
            }}
            onBlur={() => setWebsiteUrl((v) => ensureHttps(v))}
            required
            className={styles.input}
            disabled={isLoading}
          />
          <span className={styles.hint}>{t('form.websiteHint')}</span>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="focusNotes" className={styles.label}>{t('form.focusLabel')}</label>
          <textarea
            id="focusNotes"
            value={focusNotes}
            onChange={(e) => setFocusNotes(e.target.value)}
            placeholder={t('form.focusPlaceholder')}
            className={styles.textarea}
            rows={3}
            disabled={isLoading}
            maxLength={1000}
          />
        </div>

        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <label htmlFor="timezone" className={styles.label}>{t('form.timezoneLabel')}</label>
            {timezones.length > 0 ? (
              <select
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className={styles.input}
                disabled={isLoading}
              >
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className={styles.input}
                disabled={isLoading}
                maxLength={80}
              />
            )}
          </div>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="preferredWindows" className={styles.label}>{t('form.windowsLabel')}</label>
          <textarea
            id="preferredWindows"
            value={preferredWindows}
            onChange={(e) => setPreferredWindows(e.target.value)}
            placeholder={t('form.windowsPlaceholder')}
            required
            className={styles.textarea}
            rows={3}
            disabled={isLoading}
            maxLength={500}
          />
          <span className={styles.hint}>{t('form.windowsHint')}</span>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <button
          type="submit"
          className={styles.submitButton}
          disabled={isLoading || !email || !hasHost || !preferredWindows}
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className={styles.spinner} />
              {t('form.processing')}
            </>
          ) : (
            <>
              {t('form.submit')}
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>
    </>
  );
}

export default function DiscoveryCallIntakePage() {
  const t = useTranslations('discoveryCallIntake');

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link href="/" className={styles.logoLink}>
          <Logo />
        </Link>
      </header>

      <main className={styles.main}>
        <div className={styles.card}>
          <Suspense fallback={null}>
            <IntakeForm />
          </Suspense>
        </div>
      </main>

      <footer className={styles.footer}>
        <span className={styles.footerNote}>{t('footerNote')}</span>
      </footer>
    </div>
  );
}
